import type { DashboardManual, ManualState } from "@arc/contracts";

/**
 * The pure model behind the Dashboard's Manuals / Favourite manuals / Ratings pages (ADR-034 D2): the legacy
 * `ManualList` + `ManualFilter` behaviour — sort keys, the advanced filters, facility grouping and the ratings
 * distribution — over the ONE light list the API ships (`GET /dashboard/manuals`). Everything here is client-side
 * and instant, as the legacy's was (the largest realm holds < 800 manuals).
 */

/** The legacy sort dropdown (Name A-Z · Status · Created · Edited) + "Favourited" for the favourites page. */
export type ListSortKey = "name" | "status" | "created" | "edited" | "favourited";
/** The legacy's three list statuses — `released` folds `confirmed` + `marked` (both are published). */
export type ListStatus = "draft" | "review" | "released";
export const LIST_STATUSES: readonly ListStatus[] = ["draft", "review", "released"];
/** Label KEYS per list status — the legacy's own `status.*` entries, so the three surfaces agree. */
export const LIST_STATUS_LABEL_KEY = {
  draft: "status.draft",
  review: "status.readyForReview",
  released: "status.published",
} as const satisfies Record<ListStatus, string>;

export function statusOf(state: ManualState): ListStatus {
  return state === "draft" ? "draft" : state === "ready_for_review" ? "review" : "released";
}
const STATUS_RANK: Record<ListStatus, number> = { draft: 0, review: 1, released: 2 };

/** The legacy "Weitere Filter" record + the toolbar's name term. `rating` 0 = any; 1..5 = the legacy band. */
export interface ManualListFilters {
  name: string;
  /** Substring of the manual's facility PATH label (the legacy matched the full path). */
  facility: string;
  /** Substring of the creator's username. */
  creator: string;
  /** Selected statuses; empty = all. */
  statuses: ListStatus[];
  createdByMe: boolean;
  assignedToMe: boolean;
  onlyLinked: boolean;
  rating: 0 | 1 | 2 | 3 | 4 | 5;
}
export const EMPTY_LIST_FILTERS: ManualListFilters = { name: "", facility: "", creator: "", statuses: [], createdByMe: false, assignedToMe: false, onlyLinked: false, rating: 0 };

/** Any ADVANCED filter set (the popover's dot + what Reset clears); the toolbar's name term is not "advanced". */
export function advancedFiltersActive(f: ManualListFilters): boolean {
  return f.facility !== "" || f.creator !== "" || f.statuses.length > 0 || f.createdByMe || f.assignedToMe || f.onlyLinked || f.rating !== 0;
}
export function anyFilterActive(f: ManualListFilters): boolean {
  return f.name.trim() !== "" || advancedFiltersActive(f);
}

/**
 * The legacy rating band: a bar / picker value `r` matches averages in `[r − 0.5, r + 0.5)`; the 5-star band is
 * closed at 5 (averages of 1..5 ratings never exceed it, nor fall below 1 — plan panel #5: the distribution's
 * `Math.round` and this predicate agree by construction).
 */
export function inRatingBand(average: number, r: 1 | 2 | 3 | 4 | 5): boolean {
  return r === 5 ? average >= 4.5 : average >= r - 0.5 && average < r + 0.5;
}

const norm = (s: string) => s.trim().toLocaleLowerCase();
const has = (hay: string | null | undefined, needle: string) => norm(hay ?? "").includes(norm(needle));

export function filterManuals(manuals: readonly DashboardManual[], f: ManualListFilters, facilityLabelOf: (m: DashboardManual) => string): DashboardManual[] {
  return manuals.filter((m) => {
    if (f.name.trim() !== "" && !has(m.name, f.name)) return false;
    if (f.facility.trim() !== "" && !has(facilityLabelOf(m), f.facility)) return false;
    if (f.creator.trim() !== "" && !has(m.creatorUsername, f.creator)) return false;
    if (f.statuses.length > 0 && !f.statuses.includes(statusOf(m.state))) return false;
    if (f.createdByMe && !m.createdByMe) return false;
    if (f.assignedToMe && !m.assignedToMe) return false;
    if (f.onlyLinked && m.parentManualId === null) return false;
    if (f.rating !== 0 && (m.rating === null || !inRatingBand(m.rating.average, f.rating))) return false;
    return true;
  });
}

/**
 * The legacy order: name and status ascend by default; created / edited / favourited show the NEWEST first by default
 * (the legacy inverted those two). `reverse` flips the default. Ties fall back to name, then id (stable).
 */
export function sortManuals(manuals: readonly DashboardManual[], key: ListSortKey, reverse: boolean): DashboardManual[] {
  const byName = (a: DashboardManual, b: DashboardManual) => a.name.localeCompare(b.name) || a.id - b.id;
  const newestFirst = (pick: (m: DashboardManual) => string | null) => (a: DashboardManual, b: DashboardManual) =>
    (pick(b) ?? "").localeCompare(pick(a) ?? "") || byName(a, b);
  const cmp: (a: DashboardManual, b: DashboardManual) => number =
    key === "name"
      ? byName
      : key === "status"
        ? (a, b) => STATUS_RANK[statusOf(a.state)] - STATUS_RANK[statusOf(b.state)] || byName(a, b)
        : key === "created"
          ? newestFirst((m) => m.createdAt)
          : key === "edited"
            ? newestFirst((m) => m.updatedAt)
            : newestFirst((m) => m.favouritedAt);
  const sorted = [...manuals].sort(cmp);
  return reverse ? sorted.reverse() : sorted;
}

export interface FacilityGroup {
  facilityId: number;
  label: string;
  manuals: DashboardManual[];
}
/** Group by facility, groups ordered by their path label (the legacy's `pathString > name`); rows keep their order. */
export function groupByFacility(manuals: readonly DashboardManual[], facilityLabelOf: (m: DashboardManual) => string): FacilityGroup[] {
  const groups = new Map<number, FacilityGroup>();
  for (const m of manuals) {
    const g = groups.get(m.facilityId) ?? { facilityId: m.facilityId, label: facilityLabelOf(m), manuals: [] };
    g.manuals.push(m);
    groups.set(m.facilityId, g);
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label) || a.facilityId - b.facilityId);
}

/** How many RATED manuals round to 5, 4, 3, 2, 1 stars (index 0 = 5 stars) — the legacy's Ratings bar chart. */
export function ratingDistribution(manuals: readonly DashboardManual[]): [number, number, number, number, number] {
  const out: [number, number, number, number, number] = [0, 0, 0, 0, 0];
  for (const m of manuals) {
    if (m.rating === null) continue;
    const stars = Math.min(5, Math.max(1, Math.round(m.rating.average)));
    const i = 5 - stars;
    out[i] = (out[i] ?? 0) + 1;
  }
  return out;
}
